import { useState } from "react";
import { Settings as SettingsIcon, Bell, Shield, Database, Eye, RefreshCw, Moon, Sun, Palette } from "lucide-react";
import { cn } from "@/lib/utils";

const SECTIONS = ["General", "Notifications", "Security", "Display"];

export default function Settings() {
  const [active, setActive] = useState("General");
  const [dark, setDark] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState("30");
  const [notifications, setNotifications] = useState({ alerts: true, incidents: true, deployments: false });

  const icons = { General: SettingsIcon, Notifications: Bell, Security: Shield, Display: Palette };

  const toggleDark = () => {
    setDark(!dark);
    document.documentElement.classList.toggle("dark");
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Settings</h2>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Sidebar tabs */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-2 h-fit">
          {SECTIONS.map((s) => {
            const Icon = icons[s];
            return (
              <button key={s} onClick={() => setActive(s)}
                className={cn("flex items-center gap-2.5 w-full px-3 py-2 rounded text-sm transition-colors",
                  active === s ? "bg-indigo-500/15 text-indigo-400 border border-indigo-500/20" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50")}>
                <Icon size={14} strokeWidth={1.75} />
                {s}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="lg:col-span-3 bg-slate-900/50 border border-slate-800 rounded-md p-5 space-y-6 animate-fade-in">
          {active === "General" && (
            <>
              <div>
                <h3 className="text-sm font-semibold text-slate-200 mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>General Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-slate-800">
                    <div>
                      <p className="text-sm text-slate-200">Auto Refresh</p>
                      <p className="text-xs text-slate-500 mt-0.5">Automatically refresh cluster data</p>
                    </div>
                    <button onClick={() => setAutoRefresh(!autoRefresh)}
                      className={cn("w-10 h-5 rounded-full relative transition-colors flex-shrink-0",
                        autoRefresh ? "bg-indigo-500" : "bg-slate-700")}>
                      <div className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform",
                        autoRefresh ? "left-5.5 translate-x-0.5" : "left-0.5")} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-slate-800">
                    <div>
                      <p className="text-sm text-slate-200">Refresh Interval</p>
                      <p className="text-xs text-slate-500 mt-0.5">How often to refresh metrics</p>
                    </div>
                    <select value={refreshInterval} onChange={(e) => setRefreshInterval(e.target.value)}
                      className="bg-slate-800 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-slate-300 outline-none">
                      {["15", "30", "60", "120"].map((v) => <option key={v} value={v}>{v}s</option>)}
                    </select>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm text-slate-200">Default Cluster</p>
                      <p className="text-xs text-slate-500 mt-0.5">Cluster selected on app load</p>
                    </div>
                    <select className="bg-slate-800 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-slate-300 outline-none">
                      <option>prod-us-east</option>
                      <option>staging-eu-west</option>
                      <option>dev-local</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {active === "Notifications" && (
            <>
              <h3 className="text-sm font-semibold text-slate-200 mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Notification Preferences</h3>
              <div className="space-y-4">
                {Object.entries(notifications).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0">
                    <div>
                      <p className="text-sm text-slate-200 capitalize">{key === "deployments" ? "Deployment Events" : key === "alerts" ? "Alert Notifications" : "Incident Updates"}</p>
                      <p className="text-xs text-slate-500 mt-0.5">Get notified for {key}</p>
                    </div>
                    <button onClick={() => setNotifications((prev) => ({ ...prev, [key]: !val }))}
                      className={cn("w-10 h-5 rounded-full relative transition-colors flex-shrink-0",
                        val ? "bg-indigo-500" : "bg-slate-700")}>
                      <div className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform",
                        val ? "left-5" : "left-0.5")} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {active === "Security" && (
            <>
              <h3 className="text-sm font-semibold text-slate-200 mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Security Settings</h3>
              <div className="space-y-4">
                {[
                  { label: "API Key Authentication", desc: "Require API keys for cluster access", val: true },
                  { label: "RBAC Enforcement", desc: "Role-based access control for resources", val: true },
                  { label: "Audit Logging", desc: "Log all API interactions", val: false },
                ].map(({ label, desc, val }) => (
                  <div key={label} className="flex items-center justify-between py-3 border-b border-slate-800 last:border-0">
                    <div>
                      <p className="text-sm text-slate-200">{label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                    </div>
                    <button className={cn("w-10 h-5 rounded-full relative transition-colors flex-shrink-0", val ? "bg-indigo-500" : "bg-slate-700")}>
                      <div className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white", val ? "left-5" : "left-0.5")} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {active === "Display" && (
            <>
              <h3 className="text-sm font-semibold text-slate-200 mb-4" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Display Settings</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-800">
                  <div>
                    <p className="text-sm text-slate-200">Dark Mode</p>
                    <p className="text-xs text-slate-500 mt-0.5">Use dark color scheme</p>
                  </div>
                  <button onClick={toggleDark}
                    className={cn("flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs transition-colors",
                      dark ? "border-indigo-500/40 text-indigo-400 bg-indigo-500/10" : "border-slate-700 text-slate-400 hover:bg-slate-800")}>
                    {dark ? <Moon size={12} /> : <Sun size={12} />}
                    {dark ? "Dark" : "Light"}
                  </button>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-800">
                  <div>
                    <p className="text-sm text-slate-200">Dense Layout</p>
                    <p className="text-xs text-slate-500 mt-0.5">Compact view for more data density</p>
                  </div>
                  <button className="w-10 h-5 rounded-full relative transition-colors flex-shrink-0 bg-slate-700">
                    <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white" />
                  </button>
                </div>
                <div className="py-3">
                  <p className="text-sm text-slate-200 mb-3">Accent Color</p>
                  <div className="flex gap-2">
                    {["#6366f1", "#22d3ee", "#34d399", "#f59e0b", "#f87171", "#8b5cf6"].map((c) => (
                      <button key={c} className={cn("w-6 h-6 rounded-full border-2 transition-transform active:scale-90",
                        c === "#6366f1" ? "border-white" : "border-transparent hover:border-slate-500")}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* About */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>KubeAnalytics <span className="font-mono text-slate-400">v1.0.0</span></span>
          <span>Powered by Claude Sonnet · React · FastAPI · MongoDB</span>
        </div>
      </div>
    </div>
  );
}
